// src/pages/user-profile/index.tsx
/**
 * UserProfile Component
 * Manages user onboarding and profile attribute updates.
 */

import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLayoutContext } from "../../layouts/general-layout";
import { RouterConstant } from "../../routers/index";

import { UserService } from "../../services/user/user.service";
import { ExerciseService } from "../../services/display-exercise/display-exercise.service";
import { SearchExerciseService } from "../../services/search-exercise/search-exercise.service";

import "./user-profile.scss";
import ChatHeader from "../../components/chat-header";
import Dropdown from "../../components/dropdown";
import MultiSelectDropdown from "../../components/multi-select";
import Button from "../../components/button";
import { arrowDown, PointIcon, Loader } from "../../assets/images/index";
import { useUserStore } from "../../utils/userStore";
import PhoneInput from "../../components/phone-input";
import { useLocation } from "react-router-dom";
import { OTPInput, SlotProps } from 'input-otp';
import { UserType } from "../../types/userType";
import { getRequiredRoute } from "../../utils/navigationUtils";

export default function UserProfile() {
  const { t } = useLayoutContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, user_attributes, isOnboarded, isProfileComplete, navigationReturnPath, setNavigationReturnPath } = useUserStore();
  const profileComplete = isProfileComplete();
  const { ROUTERS_PATH } = RouterConstant;

  const userService = useMemo(() => new UserService(), []);
  const exerciseService = useMemo(() => new ExerciseService(), []);
  const searchExerciseService = useMemo(() => new SearchExerciseService(), []);

  const userType = user_attributes?.user_type;
  const fromMenu = location.state?.fromMenu === true;
  const fromV2Onboarding = location.state?.fromV2Onboarding === true;
  const isActiveSubscriber = useMemo(() => !!userInfo?.email && !!userInfo?.is_access, [userInfo]);

  // Dropdown data
  const [teacherOptions, setTeacherOptions] = useState<{ label: string; value: string }[]>([]);
  const [classOptions, setClassOptions] = useState<{ label: string; value: number }[]>([]);
  const [levelOptions, setLevelOptions] = useState<{ value: number; label: string }[]>([]);



  // Selected values
  const [selectedOrgType, setselectedOrgType] = useState<number | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<number | null>(null);
  const [selectedOrgLabel, setSelectedOrgLabel] = useState("");

  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [teachers, setTeachers] = useState<string[]>([]);
  const [mathUnits, setMathUnits] = useState<number | null>(null);
  const [gender, setGender] = useState<number | null>(null);
  const [countryCode, setCountryCode] = useState("+972");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [orgCode, setOrgCode] = useState("");
  const [loading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [isLoadingOrg, setIsLoadingOrg] = useState(true);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isLoadingLevels, setIsLoadingLevels] = useState(false);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(false);
  const [genderError, setGenderError] = useState(false);
  const [classError, setClassError] = useState(false);
  const [levelError, setLevelError] = useState(false);
  const [teacherError, setTeacherError] = useState(false);
  const [isMuteActive, setIsMuteActive] = useState<boolean>(false);




  // Fetch orgs and classes
  useEffect(() => {
    Promise.all([
      fetchOrganization(),
      fetchClasses(),
      fetchLevels()
    ]);

    if (user_attributes) {
      setSelectedClass(user_attributes.rel_classes ? Number(user_attributes.rel_classes) : null);
      if (user_attributes.rel_teachers && typeof user_attributes.rel_teachers === "string") {
        try {
          const parsedTeachers = JSON.parse(user_attributes.rel_teachers);
          const validTeachers = Array.isArray(parsedTeachers)
            ? parsedTeachers.map((t: any) => t)
            : [];
          setTeachers(validTeachers);
        } catch (e) {
          console.error("Failed to parse rel_teachers:", e);
          setTeachers([]);
        }
      }
      setMathUnits(user_attributes.rel_exercise_levels ? Number(user_attributes.rel_exercise_levels) : null);

      setGender(user_attributes.gender ? Number(user_attributes.gender) : null);
      setPhoneNumber(userInfo?.phone || "");
    }
  }, []);

  useEffect(() => {
    if (user_attributes?.isEnableAudio !== undefined) {
      setIsMuteActive(user_attributes.isEnableAudio === true || user_attributes.isEnableAudio === "true");
    }
  }, [user_attributes?.isEnableAudio]);

  const fetchOrganization = async () => {
    if (!userInfo?.org_id) {
      setIsLoadingOrg(false);
      return;
    }
    setIsLoadingOrg(true);
    try {
      const org = await userService.getOrganizations_By_OrgId(userInfo.org_id);
      if (org) {
        setSelectedOrg(org.id);
        setSelectedOrgLabel(org.name);
        setselectedOrgType(org.type);
      }
    } catch (error) {
      console.error("Error fetching organization:", error);
    } finally {
      setIsLoadingOrg(false);
    }
  };

  // Fetch classes
  const fetchClasses = async () => {
    setIsLoadingClasses(true);
    try {
      const classes = await exerciseService.fetchClasess();
      const options = classes.map((cls: any) => ({
        value: cls.id,
        label: cls.name,
      }));
      setClassOptions(options);

      // Save the name of the selected class to the store for other components
      const currentClassId = useUserStore.getState().user_attributes.rel_classes;
      const currentClassName = options.find((o: any) => o.value === Number(currentClassId))?.label;
      if (currentClassName) {
        useUserStore.getState().updateAttribute('rel_classes_name', currentClassName);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setIsLoadingClasses(false);
    }
  };




  // Fetch teachers when org is selected (only for school students)
  useEffect(() => {
    if (!selectedOrg || userType !== UserType.SCHOOL_USER) return;
    fetchTeachers();
  }, [selectedOrg, userType]);

  async function fetchTeachers() {
    setIsLoadingTeachers(true);
    try {
      const teachers = await userService.getTeachers(Number(selectedOrg));
      setTeacherOptions(teachers.map((t: any) => ({
        value: t.id,
        label: `${t.first_name} ${t.last_name}`
      })));
    } catch (error) {
      console.error("Failed to fetch teachers", error);
    } finally {
      setIsLoadingTeachers(false);
    }
  }

  const fetchLevels = async () => {
    setIsLoadingLevels(true);
    try {
      const levels = await searchExerciseService.getLevels();
      setLevelOptions(levels.map((l: any) => ({
        value: l.id,
        label: l.name,
      })));
    } catch (error) {
      console.error("Failed to fetch levels", error);
    } finally {
      setIsLoadingLevels(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (orgCode.length === 5 && userType === UserType.SCHOOL_USER) {
        validateCode();
      }
    }, 100); // 500ms debounce

    return () => clearTimeout(timer);
  }, [orgCode, userType]);


  const validateCode = async () => {
    setSelectedOrg(null);
    setSelectedOrgLabel("");
    if (orgCode.length === 5 && userType === UserType.SCHOOL_USER) {
      setIsValidatingCode(true);
      setCodeError(null);
      try {
        const response = await userService.validateOrganizationCode(orgCode);
        if (response && response.org_id && response.org_name) {
          setSelectedOrg(response.org_id);
          setSelectedOrgLabel(response.org_name);
          setselectedOrgType(response.org_type)
          setCodeError(null);
        }
      } catch (err: any) {
        console.error("Code validation failed:", err);
        setCodeError(t("invalidOrganizationCode"));
      } finally {
        setIsValidatingCode(false);
      }
    } else if (orgCode.length < 5) {
      setCodeError(null);
    }
  };


  const genderOptions = [
    { value: 2, label: t("female") },
    { value: 1, label: t("male") }
  ];

  const handleToggleAudio = async () => {
    const newValue = !isMuteActive;
    setIsMuteActive(newValue);
    try {
      await userService.updateUserAttribute("isEnableAudio", newValue);
      useUserStore.getState().updateAttribute("isEnableAudio", newValue);
    } catch (error) {
      console.error("Error updating audio preference", error);
    }
  };


  const handleSubmit = async () => {
    if (phoneNumber && !isPhoneValid) {
      setError(t("invalidPhoneNumber"));
      return;
    }

    let hasError = false;
    setGenderError(false);
    setClassError(false);
    setLevelError(false);
    setTeacherError(false);

    if (!gender) {
      setGenderError(true);
      hasError = true;
    }
    if (!selectedClass) {
      setClassError(true);
      hasError = true;
    }
    if (!mathUnits) {
      setLevelError(true);
      hasError = true;
    }
    if (userType === UserType.SCHOOL_USER && (!teachers || teachers.length === 0)) {
      setTeacherError(true);
      hasError = true;
    }

    if (hasError) {
      setError(t("mandatoryFieldsMissing"));
      return;
    }

    setIsLoading(true);
    setError(null);
    const payload: any = {
      user_id: userInfo.userId,
      gender,
      phone: phoneNumber,
      rel_classes: selectedClass,
      rel_exercise_levels: mathUnits,
      org_code: orgCode,
    };

    if (userType === UserType.SCHOOL_USER) {
      payload.rel_teachers = teachers;
    }
    try {
      const response = await userService.updateUserProfile(payload);
      const { id, first_name, last_name, email, phone, attributes: user_attributes, org_id, org_type } = response;
      const { setUserInfo, setAttributes } = useUserStore.getState();
      setUserInfo({ ...userInfo, userId: id, first_name, last_name, email, phone, org_id, org_type });
      if (Array.isArray(user_attributes)) {
        const currentMuteState = useUserStore.getState().user_attributes["isEnableAudio"];
        setAttributes(user_attributes);
        // Preserve isEnableAudio if missing in response
        if (currentMuteState !== undefined && !user_attributes.some((attr: any) => attr.att_name === "isEnableAudio")) {
          useUserStore.getState().updateAttribute("isEnableAudio", currentMuteState);
        }
      }
      const loginCount = user_attributes?.find((attr: any) => attr.att_name === 'login_count')?.att_value || 0;
      let shouldShowVideo = false;
      if (loginCount <= 1) {
      }

      if (navigationReturnPath) {
        const returnPath = navigationReturnPath;
        setNavigationReturnPath(null);
        navigate(returnPath, { state: { fromProfileUpdate: true } });
      } else {
        const nextRoute = getRequiredRoute(useUserStore.getState());
        navigate(nextRoute, { state: { fromProfileUpdate: true } });
      }


    } catch (err) {
      console.error("Update failed:", err);
      setError(t("updateFailedTryAgain"));
    } finally {
      setIsLoading(false);
    }
  };

  const OTPSlot = (props: SlotProps) => {
    const isFilled = props.char !== null;
    return (
      <div className={`otp-slot ${props.isActive ? 'active' : ''}`}>
        <span className={`otp-char ${isFilled ? 'filled' : 'placeholder'}`}>
          {isFilled ? props.char : '•'}
        </span>
      </div>
    );
  };

  return (
    <div className="user-profile-page">
      <ChatHeader
        chatHeading={t("personalDetails")}
        onBackClick={() => {
          if (!profileComplete) return;
          if (navigationReturnPath) {
            const returnPath = navigationReturnPath;
            setNavigationReturnPath(null);
            navigate(returnPath, { state: { fromProfileUpdate: true } });
          } else if (fromV2Onboarding) {
            navigate(ROUTERS_PATH.V2_ONBOARDING, { state: { fromProfileUpdate: true } });
          } else if (isActiveSubscriber) {
            navigate(ROUTERS_PATH.USER_MAIN_DASHBOARD);
          } else {
            navigate(ROUTERS_PATH.CHOOSEEXERCISE);
          }
        }}
        hideBack={!profileComplete || (!fromMenu && !fromV2Onboarding && !isActiveSubscriber)}
      />

      <div className="user-profile-wrapper">
        <div className="user-profile-content">
          <div className="gender-selection-wrapper">
            <div className="gender-selection-label">
              {t("gender")} <span className="required-asterisk">*</span>
            </div>
            <div className="gender-buttons">
              {genderOptions.map((option) => (
                <Button
                  key={option.value}
                  text={option.label}
                  className="book-button"
                  outline={gender !== option.value}
                  onClick={() => {
                    setGender(option.value);
                    setGenderError(false);
                  }}
                  size="sm"
                />
              ))}
            </div>
            {genderError && <div className="error-text">{t("enterGender")}</div>}
          </div>

          {/* Phone */}
          <PhoneInput
            phone={phoneNumber}
            countryCode={countryCode}
            onPhoneChange={(val) => setPhoneNumber(val)}
            onCountryCodeChange={setCountryCode}
            onValidChange={setIsPhoneValid}
          />
          {!isPhoneValid && <div className="error-text">{t("invalidPhoneNumber")}</div>}
          <div className="phone-label">
            <span className="phone-icon">
              <img src={PointIcon} alt="" />
            </span>
            <span>{t("phoneInputInfo")}</span>
          </div>

          <div className="settings-section">
            <div className="setting-item">
              <span className="setting-label">{t("muteNotificationSound")}</span>
              <div
                className={`switch-container ${isMuteActive ? 'active' : ''}`}
                onClick={handleToggleAudio}
              >
                <div className="switch-handle"></div>
              </div>
            </div>
          </div>

          {userType === UserType.SCHOOL_USER && (
            isLoadingOrg ? (
              <div className="skeleton-loader"><img src={Loader} alt="loading" /></div>
            ) : selectedOrgType != 1 ? (
              <div className="otp-input-wrapper">
                <div className="otp-label-wrapper">
                  <p className="otp-label org-label-title">{t("school")}</p>
                  <p className="otp-label otp-label-org">{selectedOrgLabel}</p>
                </div>
              </div>
            ) : (
              <div className="otp-input-wrapper">
                <div className="otp-label-wrapper">
                  <p className="otp-label org-label-title">{t("schoolCode") || "Enter OTP"}</p>
                  {isValidatingCode ? (
                    <div className="skeleton-loader-inline"><img src={Loader} alt="loading" /></div>
                  ) : (
                    <p className="otp-label otp-label-org">{(selectedOrgType != 1 ? selectedOrgLabel : '')}</p>
                  )}
                </div>
                <div
                  onClick={() => {
                    if (codeError === t("invalidOrganizationCode")) {
                      setOrgCode("");
                      setCodeError(null);
                    }
                  }}
                >
                  <OTPInput
                    inputMode="text"
                    maxLength={5}
                    value={orgCode}
                    onChange={(value) => {
                      const alphanumericValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 5);
                      setOrgCode(alphanumericValue);
                      setCodeError(null);
                    }}
                    containerClassName={`otp-container ${codeError ? 'invalid' : ''}`}
                    render={({ slots }) => (
                      <div className="otp-box">
                        {slots.map((slot, idx) => (
                          <OTPSlot key={idx} {...slot} />
                        ))}
                      </div>
                    )}
                    disabled={isValidatingCode}
                  />
                </div>
                <div className="otp-error-text-wrapper">
                  {codeError && <div className="error-text">{codeError}</div>}
                </div>
              </div>
            )
          )}


          <div className="profile-selection-detail">
            <div className="profile-selection-field">
              <div className="dropdown-wrapper">
                <Dropdown
                  label={t("mathUnit")}
                  value={mathUnits || ""}
                  onChange={(val) => {
                    setMathUnits(Number(val));
                    setLevelError(false);
                  }}
                  options={levelOptions}
                  loading={isLoadingLevels}
                  required
                />
              </div>
              {levelError && <div className="error-text">{t("enterLevel")}</div>}
            </div>

            <div className="profile-selection-field">
              <div className="dropdown-wrapper">
                <Dropdown
                  label={t("class")}
                  value={selectedClass || ""}
                  onChange={(val) => {
                    setSelectedClass(Number(val));
                    setClassError(false);
                  }}
                  options={classOptions}
                  loading={isLoadingClasses}
                  required
                />
              </div>
              {classError && <div className="error-text">{t("enterClass")}</div>}
            </div>

            {userType === UserType.SCHOOL_USER && (
              <div className="profile-selection-field custom">
                <div className="dropdown-wrapper">
                  <MultiSelectDropdown
                    label={t("teacher")}
                    value={teachers}
                    onChange={(val) => {
                      setTeachers(val);
                      if (val && val.length > 0) {
                        setTeacherError(false);
                      }
                    }}
                    options={teacherOptions}
                    disabled={selectedOrgLabel.toLowerCase() === "shoonit"}
                    loading={isLoadingTeachers}
                    required
                  />
                </div>
                {teacherError && <div className="error-text">{t("enterTeacher")}</div>}
              </div>
            )}
          </div>

        </div>

        {/* Submit */}
        <div className="next-button">
          <Button
            text={isActiveSubscriber ? t("save") : t("next")}
            color="blue"
            onClick={() => handleSubmit()}
            loader={loading}
            disabled={loading || (userType === UserType.SCHOOL_USER && selectedOrgType === 1)}
          />
        </div>
      </div>
    </div>
  );
}
